import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../database/config';
import { useHomeData } from '../../../database/HomeDataContext';
import { Body8Skeleton } from '../../Skeletons/Skeletons';
import { FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getPostSlug } from '../../../utils/slugify';

// Category colors matching Blog page
const categoryColors = {
    "Health": "bg-green-100 text-green-700",
    "Lifestyle": "bg-blue-100 text-blue-700",
    "Food Safety": "bg-red-100 text-red-700",
    "Environment": "bg-emerald-100 text-emerald-700",
    "Recipes": "bg-orange-100 text-orange-700",
    "Tips": "bg-purple-100 text-purple-700",
    "Stories": "bg-pink-100 text-pink-700",
    "Food": "bg-amber-100 text-amber-700",
    "Technology": "bg-indigo-100 text-indigo-700",
    "default": "bg-orange-100 text-orange-700"
};

const Body8 = () => {
    const { data: homeData, isOutsideProvider } = useHomeData();
    const [localData, setLocalData] = useState(null);
    const scrollContainerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    // Fallback fetch for when used outside HomeDataProvider
    useEffect(() => {
        if (!isOutsideProvider) return;
        const fetchBody8Data = async () => {
            try {
                const [headingSnap, blogsSnap] = await Promise.all([
                    getDoc(doc(db, 'home', 'blogsSection')),
                    getDocs(collection(db, 'Blogs')),
                ]);
                const blogs = [];
                blogsSnap.forEach((d) => {
                    const docId = d.id;
                    if (docId.startsWith('card')) {
                        const data = d.data();
                        blogs.push({
                            id: docId,
                            heading: data.heading || '',
                            author: data.author || 'Anonymous',
                            authorImg: data.authorImg || '',
                            date: data.date || '',
                            img: data.img || '',
                            alt: data.alt || '',
                            title: data.title || '',
                            slug: data.slug || '',
                            type: data.type || 'General',
                        });
                    }
                });
                blogs.sort((a, b) => {
                    const numA = parseInt(a.id.replace('card', '')) || 0;
                    const numB = parseInt(b.id.replace('card', '')) || 0;
                    return numA - numB;
                });
                setLocalData({
                    heading: (headingSnap.exists() && headingSnap.data().heading) || '',
                    blogs,
                });
            } catch (error) {
                console.error('Error fetching Body8 data:', error);
            }
        };
        fetchBody8Data();
    }, [isOutsideProvider]);

    const body8Data = isOutsideProvider ? localData : homeData?.body8;
    const blogs = useMemo(() => {
        const list = body8Data?.blogs || [];
        return [...list].sort((a, b) => {
            const numA = parseInt(a.id.replace('card', '')) || 0;
            const numB = parseInt(b.id.replace('card', '')) || 0;
            return numB - numA;
        });
    }, [body8Data?.blogs]);

    const checkScrollButtons = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    // Reset scroll position to start when blogs load
    useEffect(() => {
        if (scrollContainerRef.current && blogs.length > 0) {
            scrollContainerRef.current.scrollTo({ left: 0, behavior: 'instant' });
        }
        const timer = setTimeout(() => {
            checkScrollButtons();
        }, 100);
        return () => clearTimeout(timer);
    }, [blogs]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollButtons);
            window.addEventListener('resize', checkScrollButtons);
            return () => {
                container.removeEventListener('scroll', checkScrollButtons);
                window.removeEventListener('resize', checkScrollButtons);
            };
        }
    }, []);

    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const scrollAmount = 380;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    if (!body8Data || blogs.length === 0) {
        return <Body8Skeleton />;
    }

    return (
        <section className="py-10 sm:py-12 lg:py-16 bg-white overflow-hidden">
            {/* Heading + Navigation */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-6 mb-10 sm:mb-14">
                <div className="flex items-center justify-between">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
                        {body8Data.heading}
                    </h2>

                    {/* Navigation Arrows */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => scroll('left')}
                            disabled={!canScrollLeft}
                            className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${canScrollLeft
                                ? 'border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white'
                                : 'border-gray-200 text-gray-300 cursor-not-allowed'
                                }`}
                            aria-label="Scroll left"
                        >
                            <FaChevronLeft className="text-sm" />
                        </button>
                        <button
                            onClick={() => scroll('right')}
                            disabled={!canScrollRight}
                            className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${canScrollRight
                                ? 'border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white'
                                : 'border-gray-200 text-gray-300 cursor-not-allowed'
                                }`}
                            aria-label="Scroll right"
                        >
                            <FaChevronRight className="text-sm" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Horizontal Scrolling Blog Cards */}
            <div className="max-w-7xl xl:max-w-[1350px] 2xl:max-w-[1450px] mx-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 relative">
                {/* Right fade overlay */}
                <div className="absolute right-0 top-0 bottom-4 w-16 sm:w-24 md:w-32 bg-gradient-to-l from-white via-white/40 to-transparent pointer-events-none z-10"></div>

                <div
                    ref={scrollContainerRef}
                    className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory pb-4 -mr-4 sm:-mr-6 md:-mr-8 lg:-mr-10 xl:-mr-12 pr-16 sm:pr-24 md:pr-32"
                >
                    {/* Actual Blog Cards */}
                    {blogs.map((blog) => {
                        const categoryClass = categoryColors[blog.type] || categoryColors["default"];
                        const blogSlug = getPostSlug(blog);
                        return (
                            <Link
                                to={`/blog/${blogSlug}`}
                                key={blog.id}
                                className="flex-shrink-0 w-[340px] sm:w-[360px] group snap-start snap-always"
                            >
                                <article className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                                    {/* Image */}
                                    <div className="relative h-52 overflow-hidden">
                                        {blog.img ? (
                                            <img
                                                src={blog.img}
                                                alt={blog.alt || blog.heading}
                                                title={blog.title || blog.heading}
                                                width={360}
                                                height={208}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                                                <span className="text-orange-300 text-6xl">📝</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="p-5 flex flex-col flex-1">
                                        {/* Category */}
                                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 w-fit ${categoryClass}`}>
                                            {blog.type}
                                        </span>

                                        {/* Title */}
                                        <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-orange-500 transition-colors duration-200">
                                            {blog.heading}
                                        </h3>

                                        {/* Author & Date */}
                                        <div className="flex items-center gap-3 mt-auto">
                                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden">
                                                {blog.authorImg ? (
                                                    <img
                                                        src={blog.authorImg}
                                                        alt={blog.author}
                                                        loading="lazy"
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <span className="text-orange-500 text-sm font-semibold">
                                                        {blog.author?.charAt(0) || 'A'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                <span className="font-medium text-gray-700">{blog.author}</span>
                                                <span>•</span>
                                                <span>{blog.date}</span>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </Link>
                        );
                    })}

                    {/* View All Blogs Card */}
                    <Link
                        to="/blog"
                        className="flex-shrink-0 w-[340px] sm:w-[360px] group snap-start snap-always"
                    >
                        <article className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col items-center justify-center min-h-[380px]">
                            <div className="text-center p-8">
                                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <FaArrowRight className="text-white text-xl" />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-2">
                                    View All Blogs
                                </h3>
                                <p className="text-white/80 text-sm">
                                    Explore more articles and insights
                                </p>
                            </div>
                        </article>
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default Body8;
